import { useState, type FormEvent } from 'react'
import { api, errorMessage } from '../api/client'
import type { CashCheck, DayBook } from '../api/types'
import { Amt, Card, Dt, Empty, Field, Note, NumberInput, PageHead, TotalBox } from '../components/ui'
import { useApi } from '../hooks/useApi'
import { today } from '../lib/format'

/**
 * The day money, and whether the drawer agrees with it.
 */
export function DailyCash() {
  const [date, setDate] = useState(today())
  const day = useApi<DayBook>(`/reports/day-book?date=${date}`, [date])
  const cashCheck = useApi<CashCheck>(`/reports/cash-check?asOf=${date}`, [date])
  const frequent = useApi<string[]>('/expenses/frequent-titles')

  const [title, setTitle] = useState('')
  const [amount, setAmount] = useState('')
  const [counted, setCounted] = useState('')
  const [error, setError] = useState<string | null>(null)

  function refresh() {
    day.reload()
    cashCheck.reload()
    frequent.reload()
  }

  async function addExpense(event: FormEvent) {
    event.preventDefault()
    setError(null)
    try {
      await api.post('/expenses', { date, title, amount })
      setTitle('')
      setAmount('')
      refresh()
    } catch (err) {
      setError(errorMessage(err))
    }
  }

  async function removeExpense(id: string) {
    await api.delete(`/expenses/${id}`)
    refresh()
  }

  async function saveCount(event: FormEvent) {
    event.preventDefault()
    setError(null)
    try {
      await api.post('/cash-counts', { date, counted, note: null })
      setCounted('')
      refresh()
    } catch (err) {
      setError(errorMessage(err))
    }
  }

  const difference =
    cashCheck.data?.expected && counted
      ? Number(counted) - Number(cashCheck.data.expected)
      : null

  return (
    <>
      <PageHead title="خرچہ اور روزانہ حساب" hint="ایک دن کے پیسے، اور گلے کی گنتی۔" />

      <Card>
        <Field label="تاریخ">
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </Field>
      </Card>

      {error && <Note kind="error">{error}</Note>}

      {day.data && (
        <>
          <div className="totals">
            <TotalBox label="نقد بکری" value={<Amt value={day.data.cashSales} />} />
            <TotalBox label="پرانی وصولی" value={<Amt value={day.data.received} />} />
            <TotalBox label="خرچہ" value={<Amt value={day.data.spent} />} />
            <TotalBox label="نقد بچا" value={<Amt value={day.data.inHand} />} accent />
          </div>

          <Note kind="info">
            اُدھار بکری <Amt value={day.data.creditSales} /> الگ ہے۔ وہ پیسے ہاتھ میں نہیں آئے، اِس
            لیے اوپر والے نقد میں شامل نہیں۔
          </Note>
        </>
      )}

      <div className="grid grid--2">
        <Card title="خرچہ لکھیں">
          <form onSubmit={addExpense}>
            <Field label="کس چیز کا">
              <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120} />
            </Field>

            {(frequent.data?.length ?? 0) > 0 && (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
                {frequent.data!.map((name) => (
                  <button
                    key={name}
                    type="button"
                    className="btn btn--ghost btn--small"
                    onClick={() => setTitle(name)}
                  >
                    {name}
                  </button>
                ))}
              </div>
            )}

            <Field label="رقم">
              <NumberInput value={amount} onChange={setAmount} placeholder="0" />
            </Field>

            <button className="btn" disabled={!title.trim() || !amount}>
              خرچہ لکھیں
            </button>
          </form>

          <div className="table-wrap" style={{ marginTop: 14 }}>
            <table>
              <tbody>
                {(day.data?.expenses ?? []).map((expense) => (
                  <tr key={expense.id}>
                    <td>{expense.title}</td>
                    <td className="num">
                      <Amt value={expense.amount} />
                    </td>
                    <td>
                      <button
                        type="button"
                        className="btn btn--danger btn--small"
                        onClick={() => removeExpense(expense.id)}
                      >
                        ڈیلیٹ
                      </button>
                    </td>
                  </tr>
                ))}
                {(day.data?.expenses.length ?? 0) === 0 && (
                  <tr>
                    <td>
                      <Empty>اِس دن کا کوئی خرچہ نہیں۔</Empty>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>

        <Card title="گلے میں کتنے ہونے چاہئیں">
          {cashCheck.data?.everCounted ? (
            <>
              <p className="field__hint">
                پچھلی گنتی <Dt value={cashCheck.data.lastCountDate} /> کو ہوئی تھی —{' '}
                <Amt value={cashCheck.data.lastCounted} />۔ اُس کے بعد سے:
              </p>

              <div className="totals">
                <TotalBox label="نقد آیا" value={<Amt value={cashCheck.data.takingsSince} />} />
                <TotalBox label="وصولی" value={<Amt value={cashCheck.data.receivedSince} />} />
                <TotalBox label="خرچہ" value={<Amt value={cashCheck.data.spentSince} />} />
              </div>

              <div className="final-line">
                <span className="final-line__label">ہونے چاہئیں</span>
                <span className="final-line__value">
                  <Amt value={cashCheck.data.expected} />
                </span>
              </div>
            </>
          ) : (
            <Note kind="warn">
              ابھی تک ایک بار بھی گنتی نہیں ہوئی، اِس لیے یہ نہیں بتایا جا سکتا کہ گلے میں کتنے ہونے
              چاہئیں۔ نیچے پہلی گنتی لکھ دیں۔
            </Note>
          )}

          <form onSubmit={saveCount} style={{ marginTop: 12 }}>
            <Field label="آج گلے میں کتنے نکلے">
              <NumberInput value={counted} onChange={setCounted} placeholder="0" />
            </Field>

            {difference !== null && (
              <Note kind={difference === 0 ? 'info' : 'warn'}>
                {difference === 0
                  ? 'حساب برابر ہے۔'
                  : difference > 0
                    ? `گنتی ${Math.abs(difference).toLocaleString('en-US')} زیادہ ہے۔`
                    : `گنتی ${Math.abs(difference).toLocaleString('en-US')} کم ہے۔`}
              </Note>
            )}

            <button className="btn" disabled={!counted}>
              گنتی لکھیں
            </button>
          </form>
        </Card>
      </div>
    </>
  )
}
